import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Floating help widget — buton circular bottom-right, click deschide popup cu FAQs.
 *
 * Folosit pe paginile autentificate (Dashboard, BrandDetail, Account).
 * Localizare: bottom-right fixed.
 */

const buildFaqs = (t) => [
  { q: t('app.help.faq_1_q'), a: t('app.help.faq_1_a') },
  { q: t('app.help.faq_2_q'), a: t('app.help.faq_2_a') },
  { q: t('app.help.faq_3_q'), a: t('app.help.faq_3_a') },
  { q: t('app.help.faq_4_q'), a: t('app.help.faq_4_a') },
  { q: t('app.help.faq_5_q'), a: t('app.help.faq_5_a') },
  { q: t('app.help.faq_6_q'), a: t('app.help.faq_6_a') },
  { q: t('app.help.faq_7_q'), a: t('app.help.faq_7_a') },
  { q: t('app.help.faq_8_q'), a: t('app.help.faq_8_a') },
];

export default function HelpWidget() {
  const { t } = useTranslation();
  const FAQS = buildFaqs(t);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        aria-label={t('app.help.toggle_aria')}
        title={t('app.help.toggle_title')}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #D4AF37 0%, #B8972E 100%)',
          color: '#0d1b2a',
          border: 'none',
          fontFamily: 'Playfair Display, serif',
          fontSize: 26,
          fontWeight: 800,
          cursor: 'pointer',
          boxShadow: '0 12px 32px rgba(201,145,17,0.4)',
          zIndex: 100,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'scale(1.08)';
          e.currentTarget.style.boxShadow = '0 16px 40px rgba(201,145,17,0.55)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(201,145,17,0.4)';
        }}
      >
        {open ? '×' : '?'}
      </button>

      {open && (
        <div
          ref={ref}
          style={{
            position: 'fixed',
            bottom: 96,
            right: 24,
            width: 'min(420px, calc(100vw - 48px))',
            maxHeight: 'calc(100vh - 140px)',
            background: '#ffffff',
            border: '1px solid rgba(201,145,17,0.25)',
            borderRadius: 16,
            boxShadow: '0 24px 64px rgba(15,23,42,0.18)',
            zIndex: 99,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            animation: 'pbHelpFadeIn 0.25s ease',
          }}
        >
          <style>{`
            @keyframes pbHelpFadeIn {
              from { opacity: 0; transform: translateY(8px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>

          {/* Header */}
          <div style={{
            padding: '18px 20px',
            background: 'linear-gradient(135deg, rgba(201,145,17,0.08), rgba(184,151,46,0.04))',
            borderBottom: '1px solid rgba(201,145,17,0.15)',
          }}>
            <div style={{
              fontFamily: 'DM Mono, monospace',
              fontSize: 10,
              letterSpacing: 3,
              color: '#8B6914',
              textTransform: 'uppercase',
              marginBottom: 4,
              fontWeight: 600,
            }}>
              {t('app.help.eyebrow')}
            </div>
            <div style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: 18,
              fontWeight: 700,
              color: '#0d1b2a',
            }}>
              {t('app.help.title')}
            </div>
          </div>

          {/* FAQs scroll */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
            {FAQS.map((f, i) => (
              <div key={i} style={{ borderBottom: '1px solid rgba(201,145,17,0.08)' }}>
                <button
                  onClick={() => setActiveIdx(activeIdx === i ? null : i)}
                  style={{
                    width: '100%',
                    padding: '14px 20px',
                    background: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    fontFamily: 'Cormorant Garamond, serif',
                    fontSize: 15,
                    fontWeight: 600,
                    color: '#0d1b2a',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(201,145,17,0.04)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span>{f.q}</span>
                  <span style={{ color: '#8B6914', fontSize: 16, transform: activeIdx === i ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
                </button>
                {activeIdx === i && (
                  <div style={{
                    padding: '0 20px 16px',
                    fontFamily: 'Cormorant Garamond, serif',
                    fontSize: 14,
                    color: '#4B5563',
                    lineHeight: 1.6,
                  }}>
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{
            padding: '14px 20px',
            background: 'rgba(201,145,17,0.04)',
            borderTop: '1px solid rgba(201,145,17,0.1)',
            fontFamily: 'DM Mono, monospace',
            fontSize: 11,
            color: '#6B7280',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}>
            <div>
              📚 <Link to="/glosar" onClick={() => setOpen(false)} style={{ color: '#8B6914', fontWeight: 600 }}>{t('app.help.footer_glossary')}</Link>
            </div>
            <div>
              📖 <Link to="/blog" onClick={() => setOpen(false)} style={{ color: '#8B6914', fontWeight: 600 }}>{t('app.help.footer_blog')}</Link>
            </div>
            <div>
              ✉️ <a href="mailto:contact@privatebrands.ro" style={{ color: '#8B6914', fontWeight: 600 }}>contact@privatebrands.ro</a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
